/**
 * Las categorias de la tienda en un menu que sale de costado.
 *
 * POR QUE UN MENU Y NO LA FILA DE BOTONES
 * ---------------------------------------
 * Abajo del buscador habia una fila de botones, uno por categoria. Con nueve
 * categorias en el telefono corria de costado y la mayoria no se veia; en la PC
 * ocupaba dos renglones entre el buscador y las ofertas. El local la saco: el
 * menu arriba a la izquierda es donde cualquier tienda pone las categorias, y
 * deja arriba solo el logo y el buscador.
 *
 * Lo elegido no queda escondido adentro del menu: la tienda lo muestra abajo
 * del buscador como una pildora que se saca con un toque (`FiltroElegido`).
 */
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { TODO_EL_CATALOGO } from "./home";
import type { CategoriaPublica } from "./logica";

export function MenuCategorias({
  categorias,
  elegida,
  onElegir,
  conInicio = false,
}: {
  categorias: readonly CategoriaPublica[];
  /** `null` es el home ("Inicio", o "Todo" si no hay home recortado). */
  elegida: string | null;
  onElegir: (slug: string | null) => void;
  /**
   * El home no muestra todo lo publicado (0093). Entonces "Inicio" es el home
   * y "Todo" es el catalogo entero (`TODO_EL_CATALOGO`). Sin recorte, el home
   * ya es todo y alcanza con "Todo".
   */
  conInicio?: boolean;
}): JSX.Element {
  const total = categorias.reduce((a, c) => a + c.cuantos, 0);

  const opciones: { slug: string | null; nombre: string; cuantos?: number }[] = [
    ...(conInicio
      ? [
          { slug: null, nombre: "Inicio" },
          { slug: TODO_EL_CATALOGO, nombre: "Todo", cuantos: total },
        ]
      : [{ slug: null, nombre: "Todo", cuantos: total }]),
    ...categorias.map((c) => ({ slug: c.slug, nombre: c.nombre, cuantos: c.cuantos })),
  ];

  return (
    <DialogPrimitive.Root>
      <DialogPrimitive.Trigger
        aria-label="Categorías"
        className="flex size-11 items-center justify-center rounded-md text-[#0A0A0A] transition-colors hover:bg-[#F2F2F2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A0A0A]"
      >
        <IconoMenu />
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed inset-y-0 left-0 z-50 flex w-[85%] max-w-[340px] flex-col bg-white text-[#0A0A0A] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left">
          <div className="flex items-center justify-between px-5 pb-2 pt-5">
            <DialogPrimitive.Title className="text-[19px] font-[700] tracking-[-0.025em]">
              Categorías
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              aria-label="Cerrar"
              className="flex size-11 items-center justify-center rounded-md hover:bg-[#F2F2F2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A0A0A]"
            >
              <IconoCerrar />
            </DialogPrimitive.Close>
          </div>
          <DialogPrimitive.Description className="sr-only">
            Elegí qué productos ver.
          </DialogPrimitive.Description>

          <nav className="min-h-0 flex-1 overflow-y-auto px-3 pb-6">
            <ul>
              {opciones.map((o) => {
                const activa = elegida === o.slug;
                return (
                  <li key={o.slug ?? "inicio"}>
                    <DialogPrimitive.Close
                      aria-current={activa ? "page" : undefined}
                      onClick={() => {
                        onElegir(o.slug);
                        window.scrollTo({ top: 0 });
                      }}
                      className={
                        "flex min-h-12 w-full items-center justify-between rounded-md px-3 text-left text-[15px] transition-colors hover:bg-[#F2F2F2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A0A0A] " +
                        (activa ? "bg-[#F2F2F2] font-semibold" : "")
                      }
                    >
                      <span>{o.nombre}</span>
                      {o.cuantos !== undefined ? (
                        <span className="text-sm tabular-nums text-[#6E6E6E]">{o.cuantos}</span>
                      ) : null}
                    </DialogPrimitive.Close>
                  </li>
                );
              })}
            </ul>
          </nav>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * Lo elegido en el menu, a la vista: "Mac ✕". Tocarlo vuelve al inicio. Sin
 * esto, con el menu cerrado no hay forma de saber por que faltan productos.
 */
export function FiltroElegido({
  nombre,
  onQuitar,
}: {
  nombre: string;
  onQuitar: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onQuitar}
      aria-label={`Dejar de ver solo ${nombre}`}
      className="mx-auto flex h-9 items-center gap-2 rounded-full bg-[#0A0A0A] pl-4 pr-3 text-sm font-semibold text-white transition-colors hover:bg-[#2A2A2A]"
    >
      {nombre}
      <IconoCerrar className="size-4" />
    </button>
  );
}

function IconoMenu(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      className="size-6"
    >
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function IconoCerrar({ className = "size-5" }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}
